import React, { useEffect, useState, type ReactNode } from 'react';
import type { Middleware, Store } from 'redux';
import AppBase from '@atlassian/jira-app-base';
import type { BaseState } from '@atlassian/jira-servicedesk-common/src/model/types';
import { ExtraMiddlewareProvider } from '@atlassian/jira-servicedesk-common/src/redux/extra-middleware';
import { createDelegationMiddleware } from '@atlassian/jira-servicedesk-common/src/redux/middleware/delegation-middleware';
import { createStore } from '@atlassian/jira-servicedesk-common/src/ui/components/servicedesk-subapp-base/create-store/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { NAVIGATE, type Navigate } from '../../navigation/actions';

export type Props = {
	children: ReactNode;
	name: string;
	baseUrl: string;
};

export const NavigateReduxMapper = ({ children, name, baseUrl }: Props) => {
	const { push } = useRouterActions();
	const [reduxState, setReduxState] = useState<
		{ store: Store; middleware: Middleware[] } | undefined
	>(undefined);

	useEffect(() => {
		if (typeof reduxState === 'undefined') {
			const initialState = {
				appProps: {
					baseUrl,
				},
			};
			const noopReducer = (state: undefined | BaseState) => state || initialState;
			// @ts-expect-error TS2345: Argument of type '{ appProps: { baseUrl: string; }; }' is not assignable to parameter of type '{ readonly appProps: { locale: Locale; baseUrl: string; projectKey?: string | undefined; projectName?: string | undefined; withBreadCrumbs?: boolean | undefined; }; }'.
			const store = createStore(name, initialState, noopReducer, []);
			const subAppActionMapper = {
				[NAVIGATE]: (action: Navigate) => {
					// HACK - this makes sure that the navigate action happens after
					// redux has updated it's store AND the prompt re-rendered
					// (src/packages/servicedesk/common/src/navigation/blocker/prompt/index.js).
					// This ensures that the navigate blocker doesn't render after they
					// click on cancel.
					// Ideally we would hook the navigate prompt directly with the store
					// so that it's not reliant on react-redux re-rendering the view.
					// However this feature is only available in react-redux v7.1 (https://react-redux.js.org/7.1/api/hooks).
					setTimeout(() => {
						push && push(action.payload.location);
					}, 0);
					return action;
				},
			};
			const middleware = [createDelegationMiddleware(store, subAppActionMapper)];
			setReduxState({
				store,
				middleware,
			});
		}
	}, [reduxState, push, baseUrl, name]);

	if (typeof reduxState === 'undefined') {
		return null;
	}

	return (
		<AppBase store={reduxState.store}>
			<ExtraMiddlewareProvider extraMiddleware={reduxState.middleware}>
				{children}
			</ExtraMiddlewareProvider>
		</AppBase>
	);
};
